import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

interface CalendarPickerProps {
  value: string; // YYYY-MM-DD format
  onChange: (date: string) => void;
  onClose: () => void;
}

export function CalendarPicker({ value, onChange, onClose }: CalendarPickerProps) {
  const { colors, theme } = useTheme();
  
  // Parse the value date or use today
  const initialDate = value ? new Date(value + 'T00:00:00') : new Date();
  const [currentMonth, setCurrentMonth] = useState(initialDate.getMonth());
  const [currentYear, setCurrentYear] = useState(initialDate.getFullYear());
  
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  // Get days in month
  const getDaysInMonth = (month: number, year: number) => {
    return new Date(year, month + 1, 0).getDate();
  };
  
  // Get first day of month (0 = Sunday)
  const getFirstDayOfMonth = (month: number, year: number) => {
    return new Date(year, month, 1).getDay();
  };
  
  // Generate calendar days
  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const days = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    
    // Add days of month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  };
  
  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };
  
  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };
  
  const handleDayClick = (day: number) => {
    const month = (currentMonth + 1).toString().padStart(2, '0');
    const dayStr = day.toString().padStart(2, '0');
    const dateString = `${currentYear}-${month}-${dayStr}`;
    onChange(dateString);
    onClose();
  };
  
  const isSelectedDay = (day: number | null) => {
    if (!day || !value) return false;
    const selectedDate = new Date(value + 'T00:00:00');
    return (
      day === selectedDate.getDate() &&
      currentMonth === selectedDate.getMonth() &&
      currentYear === selectedDate.getFullYear()
    );
  };
  
  const isToday = (day: number | null) => {
    if (!day) return false;
    const today = new Date();
    return (
      day === today.getDate() &&
      currentMonth === today.getMonth() &&
      currentYear === today.getFullYear()
    );
  };
  
  const calendarDays = generateCalendarDays();
  
  return (
    <div
      className="absolute top-full left-0 mt-2 p-4 rounded-xl shadow-lg z-50 w-80"
      style={{
        backgroundColor: colors.cardBg,
        border: `1px solid ${colors.border}`
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Month/Year Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={handlePrevMonth}
          className="p-2 rounded-lg transition-colors"
          style={{ color: colors.text }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = colors.cardBgSecondary;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        
        <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600, color: colors.heading }}>
          {monthNames[currentMonth]} {currentYear}
        </div>
        
        <button
          type="button"
          onClick={handleNextMonth}
          className="p-2 rounded-lg transition-colors"
          style={{ color: colors.text }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = colors.cardBgSecondary;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
      
      {/* Days of Week */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {daysOfWeek.map((day) => (
          <div
            key={day}
            className="text-center text-xs py-2"
            style={{ color: colors.textSecondary, fontWeight: 600 }}
          >
            {day}
          </div>
        ))}
      </div>
      
      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map((day, index) => (
          <button
            key={index}
            type="button"
            onClick={() => day && handleDayClick(day)}
            disabled={!day}
            className="aspect-square rounded-lg text-sm transition-all"
            style={{
              backgroundColor: isSelectedDay(day)
                ? '#6366F1'
                : isToday(day)
                ? theme === 'dark'
                  ? 'rgba(99, 102, 241, 0.15)'
                  : '#EEF2FF'
                : 'transparent',
              color: isSelectedDay(day)
                ? '#FFFFFF'
                : day
                ? colors.text
                : 'transparent',
              fontWeight: isSelectedDay(day) || isToday(day) ? 600 : 400,
              cursor: day ? 'pointer' : 'default'
            }}
            onMouseEnter={(e) => {
              if (day && !isSelectedDay(day)) {
                e.currentTarget.style.backgroundColor = colors.cardBgSecondary;
              }
            }}
            onMouseLeave={(e) => {
              if (day && !isSelectedDay(day)) {
                e.currentTarget.style.backgroundColor = isToday(day)
                  ? theme === 'dark'
                    ? 'rgba(99, 102, 241, 0.15)'
                    : '#EEF2FF'
                  : 'transparent';
              }
            }}
          >
            {day}
          </button>
        ))}
      </div>
      
      {/* Today Button */}
      <div className="mt-4 pt-3" style={{ borderTop: `1px solid ${colors.border}` }}>
        <button
          type="button"
          onClick={() => {
            const today = new Date();
            const month = (today.getMonth() + 1).toString().padStart(2, '0');
            const day = today.getDate().toString().padStart(2, '0');
            const dateString = `${today.getFullYear()}-${month}-${day}`;
            onChange(dateString);
            onClose();
          }}
          className="w-full py-2 px-4 rounded-lg text-sm transition-colors"
          style={{
            color: '#6366F1',
            backgroundColor: theme === 'dark' ? 'rgba(99, 102, 241, 0.1)' : '#EEF2FF',
            fontWeight: 600
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgba(99, 102, 241, 0.2)' : '#DDD6FE';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgba(99, 102, 241, 0.1)' : '#EEF2FF';
          }}
        >
          Today
        </button>
      </div>
    </div>
  );
}
